import React, { useState, useRef, useEffect } from "react";
import { Send, RefreshCw, MessageSquare } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function IvrDemo() {
  const { t, lang } = useI18n();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [chatLang, setChatLang] = useState(lang);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/ivr/chat", { session_id: sessionId, message: msg, language: chatLang });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: "(error) " + (err.response?.data?.detail || err.message) }]);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setMessages([]); setSessionId(crypto.randomUUID()); };

  return (
    <div className="space-y-6" data-testid="demo-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="chip"><MessageSquare className="h-3 w-3" /> {t("demo.title")}</div>
          <h1 className="mt-3 text-3xl lg:text-4xl font-extrabold text-white tracking-tight">{t("demo.title")}</h1>
          <p className="mt-1 text-slate-400 text-sm">{t("demo.sub")}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-bg-2 rounded-full p-1 border border-white/5">
            <button onClick={() => setChatLang("mn")} className={`px-3 py-1 text-xs font-bold rounded-full ${chatLang === "mn" ? "bg-primary text-white" : "text-slate-400"}`} data-testid="demo-lang-mn">МН</button>
            <button onClick={() => setChatLang("en")} className={`px-3 py-1 text-xs font-bold rounded-full ${chatLang === "en" ? "bg-primary text-white" : "text-slate-400"}`} data-testid="demo-lang-en">EN</button>
          </div>
          <button onClick={reset} className="btn-ghost inline-flex items-center gap-2" data-testid="demo-reset"><RefreshCw className="h-4 w-4" /> {t("demo.reset")}</button>
        </div>
      </div>

      <div className="glass rounded-3xl flex flex-col overflow-hidden" style={{ height: "calc(100vh - 240px)", minHeight: 400 }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 lg:p-10 space-y-4">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="h-20 w-20 rounded-full bg-gradient-to-br from-primary to-magenta shadow-glow-lg animate-orb-float flex items-center justify-center">
                <MessageSquare className="h-8 w-8 text-white" />
              </div>
              <div className="mt-6 text-slate-400 max-w-md text-sm">{t("demo.hint")}</div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${m.role === "user" ? "bg-bg-2 text-white rounded-tr-sm" : "bg-gradient-to-br from-primary to-primary-700 text-white rounded-tl-sm shadow-lg shadow-primary/30"}`} data-testid={`demo-msg-${m.role}-${i}`}>
                <div className="whitespace-pre-wrap leading-relaxed text-sm">{m.content}</div>
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="px-4 py-3 rounded-2xl rounded-tl-sm bg-gradient-to-br from-primary to-primary-700 text-white inline-flex items-center gap-3" data-testid="demo-thinking">
                <div className="wave-bars"><span/><span/><span/><span/><span/></div>
                <span className="text-xs">{t("demo.thinking")}…</span>
              </div>
            </div>
          )}
        </div>
        <form onSubmit={send} className="border-t border-white/5 p-4 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t("demo.placeholder")}
            className="input-dark flex-1"
            data-testid="demo-input"
            disabled={busy}
          />
          <button type="submit" disabled={busy || !input.trim()} className="btn-primary inline-flex items-center gap-2" data-testid="demo-send">
            <Send className="h-4 w-4" /> {t("demo.send")}
          </button>
        </form>
      </div>
    </div>
  );
}
